import type { LocaleCode } from '#types/locale'
import type { ChatPlugin } from '#types/plugin/chat'
import type { MIAWConfig, MIAWEventName, PrechatFields, ScriptLoadResult } from '#types/plugin/enhancedLivechat'
import { isString } from '#core/utils/is'

export default (config: MIAWConfig): ChatPlugin => {
  const { $feature, $gtm, $locale } = useNuxtApp()
  const {
    baseUrlBot,
    baseUrlLiveAgent,
    bootstrapUrlBot,
    bootstrapUrlLiveAgent,
    orgId,
    scrtUrl
  } = $feature.configLivechat
  const available = ref<boolean>(false)
  const { targetEnv } = useRuntimeConfig().public
  const { loadPageDataPollInterval } = useAppConfig().livechat
  const route = useRoute()

  const _targetEnv = targetEnv.toUpperCase()
  const isBot = config.botLocales?.includes($locale)
  let initializationComplete = false
  let prechatFieldsSet = false
  let timerId

  const languageToSkillId = (locale: LocaleCode) => {
    const baseLanguage = locale.split('-')[0]
    const languageMap = {
      cs: 'Chat_English',
      da: 'Chat_English',
      de: 'Chat_German',
      en: 'Chat_English',
      es: 'Chat_Spanish',
      fr: 'Chat_French',
      it: 'Chat_Italian',
      nl: 'Chat_Dutch',
      pl: 'Chat_Polish',
      pt: 'Chat_Portuguese',
      sv: 'Chat_Swedish',
    }
    return languageMap[baseLanguage] || 'Chat_English'
  }

  const isHomepage = computed(() => {
    const normalized = route.path.replace(/\/+$/, '')
    return /^\/(?:[a-z]{2}(?:-[a-z]{2})?)?$/.test(normalized)
  })

  const isCatalogOrSRP = computed(() => isString(route.name) && ['c-slug', 'search'].includes(route.name))

  const setChatVisibility = () => {
    const toggleBodyClass = (className, condition) => document.body.classList.toggle(className, condition)
    toggleBodyClass('commerce-page', isCatalogOrSRP.value)
    toggleBodyClass('hide-chat-button', isHomepage.value)
  }

  const loadCss = () => {
    const cssPath = config.cssPath
    try {
      if (!document.querySelector(`link[href="${cssPath}"]`)) {
        useHead({
          link: () => [
            {
              rel: 'stylesheet',
              type: 'text/css',
              href: cssPath,
              once: true
            }
          ],
        })
      }
    }
    catch (err: any) {
      log.error('failed to load livechat css', err)
    }
  }

  const formatChatOrigin = (locale: LocaleCode) => {
    const lowerCaseLocale = locale.toLowerCase()
    const [language = '', country = ''] = lowerCaseLocale.split('-')
    return country && language ? `${country}-${language}` : lowerCaseLocale
  }

  const setPrechatFields = async (): Promise<boolean> => {
    const browserLanguage = navigator.language
    const chatOrigin = formatChatOrigin($locale)

    const fields: PrechatFields = {
      Current_Bot_Language: config.languageConfig[$locale],
      Current_Bot_Skill_Id: languageToSkillId($locale),
      Brand: config.brand,
      Chat_Origin: chatOrigin,
      BrowserLanguage: browserLanguage,
    }

    try {
      if (window.embeddedservice_bootstrap?.prechatAPI) {
        window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields(fields)
        prechatFieldsSet = true
        // Debug info
        window._miawDebug = {
          lastUpdate: new Date().toISOString(),
          fields
        }
      }
      else {
        log.error('MIAW prechatAPI not available')
        prechatFieldsSet = false
      }
    }
    catch (error: any) {
      log.error('Error setting prechat fields:', error)
      prechatFieldsSet = false
    }

    return prechatFieldsSet
  }

  const initializeLiveChatEventTracking = (): void => {
    const events = {
      onEmbeddedMessagingWindowMaximized: () => $gtm?.push('livechat.onAction', 'Displayed', 'Salesforce'),
      onEmbeddedMessagingConversationClosed: () => $gtm?.push('livechat.onAction', 'Closed', 'Salesforce', false),
      onEmbeddedMessagingConversationRouted: () => $gtm?.push('livechat.onAction', 'Agent Transfer Completed', 'Salesforce'),
      onEmbeddedMessagingConversationStarted: () => {
        $gtm?.push('livechat.onAction', 'Started', 'Salesforce', false)
        $gtm?.push('livechat.onAction', 'Chatbot Engaged', 'Salesforce')
      }
    }
    Object.entries(events).forEach(([eventName, handler]) =>
      window.addEventListener(eventName as MIAWEventName, handler))
  }

  const handleMessagingReady = async (): Promise<void> => {
    // Bootstrap signaled ready, so chat can be launched. Hidden prechat
    // fields are best-effort metadata - if `prechatAPI` isn't exposed yet
    // (race with bootstrap, or deployment without prechat form), don't gate
    // chat availability on it; `open()` retries `setPrechatFields()` at
    // click-time anyway.
    available.value = true
    try {
      const success = await setPrechatFields()
      if (!success)
        log.warn('MIAW prechat fields not set on ready; will retry on open')
      else
        log.info('MIAW chat now available')
    }
    catch (error: any) {
      log.error('Error in handleMessagingReady:', error)
    }
  }

  const initializeMessaging = (): void => {
    if (initializationComplete) {
      if (timerId) {
        clearInterval(timerId)
        timerId = null
      }
      return
    }

    const loadPageDataEvent = window.dataLayer?.find((event) => event.event === 'loadPageData')

    if (loadPageDataEvent) {
      // Clear interval immediately to prevent multiple calls
      if (timerId) {
        clearInterval(timerId)
        timerId = null
      }
      initializationComplete = true

      setChatVisibility()
      loadCss()

      try {
        const bootstrap = window.embeddedservice_bootstrap
        if (!bootstrap)
          throw new Error('Embedded service bootstrap not loaded')

        bootstrap.settings.language = config.languageConfig[$locale]
        bootstrap.settings.chatButtonPosition = config.chatButtonPosition ?? '4rem,3rem'

        window.addEventListener('onEmbeddedMessagingReady', handleMessagingReady)
        bootstrap.init(
          orgId[_targetEnv],
          config.eswConfigDevName!,
          isBot ? baseUrlBot![_targetEnv] : baseUrlLiveAgent![_targetEnv],
          {
            scrt2URL: scrtUrl![_targetEnv]
          }
        )
      }
      catch (err: any) {
        log.error('Error loading Embedded Messaging:', err)
        // Reset flag on error to allow retry
        initializationComplete = false
      }
    }
  }

  const loadBootstrapScript = async (): Promise<ScriptLoadResult> => {
    try {
      window.initEmbeddedMessaging = initializeMessaging
      const result = await loadScript(isBot
        ? bootstrapUrlBot![_targetEnv]
        : bootstrapUrlLiveAgent![_targetEnv], {
        'data-miaw-chat': 'true'
      }, {
        type: 'text/javascript'
      }) as ScriptLoadResult
      if (!result.loaded)
        throw new Error('Failed to load MIAW script')

      return result
    }
    catch (error: any) {
      log.error('Error loading MIAW script:', error)
      throw error
    }
  }

  const init = async (): Promise<void> => {
    try {
      await loadBootstrapScript()
      initializeLiveChatEventTracking()
      // Poll for the `loadPageData` dataLayer event.
      timerId = window.setInterval(initializeMessaging, loadPageDataPollInterval)
      initializeMessaging()
    }
    catch (error: any) {
      log.error('Failed to initialize MIAW chat:', error)
    }
  }

  watch([isHomepage, isCatalogOrSRP], () => {
    setChatVisibility()
  }, { immediate: true })

  onUnmounted(() => {
    if (timerId) {
      window.clearInterval(timerId)
      timerId = null
    }
  })

  init()

  return {
    open: async (): Promise<void> => {
      await setPrechatFields()
      if (window.embeddedservice_bootstrap?.utilAPI)
        window.embeddedservice_bootstrap.utilAPI.launchChat()
    },
    available
  }
}
